import React, { useMemo } from "react";
import { Formik, Form } from "formik";

import { useSnackbarContext } from "widgets/SnackBar/SnackBar";
import Loader from "shared/Icons/Loader/Loader";
import { PrimaryButton } from "widgets";
import {
  useEditBranchDetails,
  useFetchCountryList,
  useFetchGradeList,
} from "store/backend/apiHooks";
import BranchForm from "components/BranchForm/BranchForm";
import {
  buildBranchPayload,
  createBranchSchema,
  normalizeBranchInitialValues,
} from "helpers/branchUtils";
import {
  getCountryOptions,
  mapGradesToOptions,
  resolveBranchUuid,
} from "helpers/tenantUtils";

import styles from "./EditBranchDetails.module.scss";

const EditBranchDetails = ({
  branchDetails,
  branchId,
  existingGradeIds,
  tenantCountryId,
}) => {
  const { openSnackbar } = useSnackbarContext();
  const { mutate: editBranchDetails, isPending: isEditBranchDetailsLoading } =
    useEditBranchDetails();
  const {
    data: gradeList,
    isPending: isGradeListLoading,
    isError: isErrorOnGradeList,
    error: errorMessageOnFetchGradeList,
  } = useFetchGradeList();
  const {
    data: countryList,
    isPending: isCountryListLoading,
  } = useFetchCountryList();

  const filteredGrades =
    gradeList?.items?.filter((grade) => existingGradeIds?.includes(grade.id)) || [];
  const gradeOptions = mapGradesToOptions(filteredGrades);
  const countryOptions = useMemo(
    () => (countryList?.items ? getCountryOptions(countryList.items) : []),
    [countryList],
  );
  const branchSchema = useMemo(
    () => createBranchSchema(countryOptions, tenantCountryId),
    [countryOptions, tenantCountryId],
  );
  const isFormContextLoading = isCountryListLoading || !tenantCountryId;

  if (!branchDetails) return null;

  return (
    <div className={styles.EditBranchDetailsContainer}>
      <div className={styles.branchDetailsContent}>
        {isFormContextLoading ? (
          <Loader inline />
        ) : (
          <Formik
            initialValues={normalizeBranchInitialValues(branchDetails, {
              countryOptions,
              tenantCountryId,
            })}
            validationSchema={branchSchema}
            validateOnMount={false}
            validateOnChange={true}
            validateOnBlur={true}
            onSubmit={(values, { setSubmitting }) => {
              const payload = buildBranchPayload(
                values,
                countryOptions,
                tenantCountryId,
                resolveBranchUuid(branchDetails, branchId),
              );
              editBranchDetails(payload, {
                onSuccess: () => {
                  setSubmitting(false);
                  openSnackbar("Branch details updated successfully!", "success");
                  window.location.reload();
                },
                onError: (error) => {
                  setSubmitting(false);
                  openSnackbar("Error updating branch details: " + error.message, "error");
                },
              });
            }}
          >
            {({
              values,
              errors,
              touched,
              dirty,
              setFieldValue,
              setFieldTouched,
              handleBlur,
              handleSubmit,
              isSubmitting,
              submitCount,
            }) => (
              <Form onSubmit={handleSubmit}>
                <BranchForm
                  values={values}
                  touched={touched}
                  dirty={dirty}
                  setFieldValue={setFieldValue}
                  setFieldTouched={setFieldTouched}
                  handleBlur={handleBlur}
                  errors={errors}
                  submitCount={submitCount}
                  gradeOptions={gradeOptions}
                  countryOptions={countryOptions}
                  tenantCountryId={tenantCountryId}
                  isGradeListLoading={isGradeListLoading}
                  isErrorOnGradeList={isErrorOnGradeList}
                  errorMessageOnFetchGradeList={errorMessageOnFetchGradeList}
                />
                <div className={styles.submitRow}>
                  <PrimaryButton
                    type="submit"
                    disabled={isSubmitting || isEditBranchDetailsLoading}
                  >
                    {isEditBranchDetailsLoading || isSubmitting ? (
                      <Loader inline />
                    ) : (
                      "Save changes"
                    )}
                  </PrimaryButton>
                </div>
              </Form>
            )}
          </Formik>
        )}
      </div>
    </div>
  );
};

export default EditBranchDetails;
